import { type QueryClient, type QueryKey, useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { ApiError, api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  NOT_SAVED_TEXT, afterRefusal, commitOf, draftsAfterSave, failureStep, leftBehindText,
} from "@/lib/call-form";
import { sameValues } from "@/lib/form";
import { useKeys } from "@/lib/queries";
import { accountScope } from "@/lib/session-cache";
import { type Draft, draftOf, ownFields } from "@/lib/record-fields";
import type { LeadFields, RecordField } from "@/lib/types";

/**
 * What the Phone page's form remembers while a rep fills it in: per lead, the answers typed and not
 * yet saved, what is wrong with the ones that could not be, and the queue that sends them.
 *
 * Module state, like the outcome a voicemail suggestion planned (`planned-outcome.ts`): the form is
 * drawn by a pane that comes and goes with the call, and an answer typed during the call has to be
 * there in the outcome window, and still be sent when the pane is gone. So the queue sends the
 * request itself and writes the answer into the query cache through what `useCallFormSaver`
 * registered, and nothing here belongs to a component.
 *
 * The rules are `lib/call-form.ts`'s; this file only holds the state they are applied to:
 *  - one request in flight per lead, and what is committed meanwhile goes in the next one, together;
 *  - a sent answer's draft is dropped only if it still reads as what was sent;
 *  - leaving a lead sends its valid drafts at once and, when that settles, says once what is left;
 *  - an answer refused because the field is no longer the rep's to change is let go of once that
 *    notice has named it, since nothing they could type would save it.
 * It belongs to the signed-in account (`accountScope`, the key the page's kept lead is held under
 * too) and is emptied when that changes.
 */

/** What is wrong with one answer: `problem` is about the value (the console's check or the
 *  server's reason), `failed` is a request that did not save it, which Try again sends once more. */
export interface FieldMark {
  kind: "problem" | "failed";
  text: string;
}

/** One lead's form as the pane reads it. Replaced, never changed in place. */
export interface CallFormView {
  /** The answers that differ from the server's, by field key. */
  drafts: Readonly<Record<string, Draft>>;
  marks: Readonly<Record<string, FieldMark>>;
  /** A request is away, or answers wait for the one that is. */
  saving: boolean;
  /** When the last save came back (`Date.now()`), or null. */
  savedAt: number | null;
}

/** The answers left unsaved on a lead the rep is no longer on. */
export interface LeftBehind {
  leadId: string;
  text: string;
}

type Sender = (leadId: string, values: Record<string, unknown>) => Promise<LeadFields>;
type Writer = (leadId: string, saved: LeadFields) => void;

interface Entry {
  draft: Draft;
  value: unknown;
}

interface Flight {
  entries: Record<string, Entry>;
  /** The second try after a 409. */
  retried: boolean;
  /** The answers a refusal did not name, sent again without the refused ones. */
  again: boolean;
}

interface LeadForm {
  drafts: Record<string, Draft>;
  marks: Record<string, FieldMark>;
  /** The marked answers whose field is no longer this person's to change. */
  gone: Set<string>;
  /** Committed answers waiting for the request in flight to come back. */
  queued: Record<string, Entry>;
  flight: Flight | null;
  savedAt: number | null;
  /** The newest definition and server value of every field this form has shown or saved. */
  fields: Map<string, RecordField>;
}

const EMPTY: CallFormView = { drafts: {}, marks: {}, saving: false, savedAt: null };

const patchFields: Sender = (leadId, values) => api.patch<LeadFields>(`/leads/${leadId}/fields`, { values });
const pause = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

let forms = new Map<string, LeadForm>();
let views = new Map<string, CallFormView>();
/** The leads being left, with the name the notice will use. */
let leaving = new Map<string, string | null>();
let notice: LeftBehind | null = null;
/** Whose answers these are (`accountScope`): undefined until a page has said. */
let account: string | undefined;
/** Goes up when the store is emptied, so a request that comes back afterwards changes nothing. */
let epoch = 0;
let write: Writer | null = null;
let send: Sender = patchFields;
let wait = pause;
const watchers = new Set<() => void>();

function watch(notify: () => void): () => void {
  watchers.add(notify);
  return () => {
    watchers.delete(notify);
  };
}

function formOf(leadId: string): LeadForm {
  let form = forms.get(leadId);
  if (!form) {
    form = { drafts: {}, marks: {}, gone: new Set(), queued: {}, flight: null, savedAt: null, fields: new Map() };
    forms.set(leadId, form);
  }
  return form;
}

const busy = (form: LeadForm) => form.flight !== null || Object.keys(form.queued).length > 0;

/** The draft of this field already sent or waiting to be, which the next commit is compared with. */
function onItsWay(form: LeadForm, key: string): Draft | undefined {
  return form.queued[key]?.draft ?? form.flight?.entries[key]?.draft;
}

/** Publishes a lead's form after a change, and drops the drafts that are the server's value again
 *  with nothing on its way and nothing wrong: they are not drafts. */
function changed(leadId: string): void {
  const form = forms.get(leadId);
  if (form) {
    for (const [key, draft] of Object.entries(form.drafts)) {
      const field = form.fields.get(key);
      if (!field || key in form.marks || onItsWay(form, key) !== undefined) continue;
      if (sameValues(draft, draftOf(field))) delete form.drafts[key];
    }
    views.set(leadId, {
      drafts: { ...form.drafts }, marks: { ...form.marks }, saving: busy(form), savedAt: form.savedAt,
    });
  }
  for (const notify of watchers) notify();
}

function unmark(form: LeadForm, key: string): void {
  delete form.marks[key];
  form.gone.delete(key);
}

/**
 * Marks a sent answer, unless the rep has moved on from it: a newer value of the same field is
 * already waiting, or they are typing over what was sent. Either way the sentence would be about a
 * value that is no longer on screen, and the newer one is sent in its turn.
 */
function mark(form: LeadForm, key: string, entry: Entry, kind: FieldMark["kind"], text: string): void {
  if (key in form.queued) return;
  const draft = form.drafts[key];
  if (draft === undefined || !sameValues(draft, entry.draft)) return;
  form.marks[key] = { kind, text };
}

// --- typing and committing -------------------------------------------------------------------------------

/** The rep changed an answer. Typing takes away what was said about the old value. */
export function setCallFormDraft(leadId: string, field: RecordField, draft: Draft): void {
  const form = formOf(leadId);
  form.fields.set(field.key, field);
  form.drafts[field.key] = draft;
  unmark(form, field.key);
  changed(leadId);
}

/**
 * The rep left an answer (or picked one, for a dropdown, a yes/no and a multi-select). Nothing
 * happens for an answer that was not touched, which is also what a Select's trigger losing focus to
 * its own list is; nor for one already marked, until it is typed over or tried again.
 */
export function commitCallFormField(leadId: string, field: RecordField): void {
  const form = forms.get(leadId);
  if (!form || form.drafts[field.key] === undefined || field.key in form.marks) return;
  form.fields.set(field.key, field);
  commit(form, field);
  changed(leadId);
  pump(leadId);
}

/**
 * Send contract saved these answers itself (ACL-604), from what the form held for them: the form lets
 * go of its drafts, its marks and what was still waiting to be sent for them, so nothing older is sent
 * over the saved value. A request already away is left to come back.
 */
export function dropCallFormAnswers(leadId: string, keys: readonly string[]): void {
  const form = forms.get(leadId);
  if (!form) return;
  for (const key of keys) {
    delete form.drafts[key];
    delete form.queued[key];
    unmark(form, key);
  }
  changed(leadId);
}

/** Try again on a failed answer: the same value, sent once more. */
export function retryCallFormField(leadId: string, field: RecordField): void {
  const form = forms.get(leadId);
  if (!form || form.drafts[field.key] === undefined) return;
  form.fields.set(field.key, field);
  unmark(form, field.key);
  commit(form, field);
  changed(leadId);
  pump(leadId);
}

function commit(form: LeadForm, field: RecordField): void {
  const draft = form.drafts[field.key];
  if (draft === undefined) return;
  const decided = commitOf(field, draft, onItsWay(form, field.key));
  if (decided.kind === "problem") {
    form.marks[field.key] = { kind: "problem", text: decided.problem };
  } else if (decided.kind === "send") {
    form.queued[field.key] = { draft, value: decided.value };
  } else if (onItsWay(form, field.key) === undefined) {
    // It reads as the server's value (spaces aside): not a draft any more.
    delete form.drafts[field.key];
  }
}

// --- the queue -------------------------------------------------------------------------------------------

function pump(leadId: string): void {
  const form = forms.get(leadId);
  if (!form) return;
  if (form.flight === null && Object.keys(form.queued).length > 0) {
    form.flight = { entries: form.queued, retried: false, again: false };
    form.queued = {};
    changed(leadId);
    request(leadId, form, form.flight);
    return;
  }
  if (!busy(form)) settleLeave(leadId);
}

function request(leadId: string, form: LeadForm, flight: Flight): void {
  const started = epoch;
  const values = Object.fromEntries(Object.entries(flight.entries).map(([key, e]) => [key, e.value]));
  // `catch` after `then`, not a second callback beside it: whatever goes wrong while the answer
  // is being taken in must also end this flight, or the lead's queue would wait on it for good.
  send(leadId, values)
    .then((saved) => {
      if (started !== epoch) return;
      // A 2xx that is not the form (an empty body, a proxy's page) saved nothing we can show.
      if (!Array.isArray((saved as LeadFields | undefined)?.sections)) {
        throw new Error("The answer to a save was not the form");
      }
      // The cache first: when the draft goes, the input falls back to the answer just saved.
      write?.(leadId, saved);
      for (const section of saved.sections) {
        for (const field of ownFields(section)) form.fields.set(field.key, field);
      }
      const sent: Record<string, Draft> = {};
      for (const [key, entry] of Object.entries(flight.entries)) {
        sent[key] = entry.draft;
      }
      form.drafts = draftsAfterSave(form.drafts, sent);
      form.savedAt = Date.now();
      form.flight = null;
      changed(leadId);
      pump(leadId);
    })
    .catch((error: unknown) => {
      // Only for the flight still away: once it has been taken in, a later slip is not its failure.
      if (started !== epoch || form.flight !== flight) return;
      failed(leadId, form, flight, error);
    });
}

function failed(leadId: string, form: LeadForm, flight: Flight, error: unknown): void {
  const status = error instanceof ApiError ? error.status : null;
  const step = failureStep(status, flight.retried);
  if (step.kind === "retry") {
    const started = epoch;
    void wait(step.afterMs).then(() => {
      if (started !== epoch) return;
      flight.retried = true;
      request(leadId, form, flight);
    });
    return;
  }
  const entries = Object.entries(flight.entries);
  form.flight = null;
  if (step.kind === "failed") {
    for (const [key, entry] of entries) mark(form, key, entry, "failed", step.text);
  } else {
    const message = error instanceof Error ? error.message : "";
    const refusal = afterRefusal(status ?? 0, message, [...form.fields.values()], Object.keys(flight.entries));
    const again: Record<string, Entry> = {};
    for (const [key, entry] of entries) {
      const why = refusal.problems[key];
      if (why !== undefined) {
        mark(form, key, entry, "problem", why);
        if (key in form.marks && refusal.gone.includes(key)) form.gone.add(key);
      } else if (refusal.failed !== null) mark(form, key, entry, "failed", refusal.failed);
      // Sent again once. A second refusal that still names somebody else leaves these not saved.
      else if (flight.again) mark(form, key, entry, "failed", NOT_SAVED_TEXT);
      else if (!(key in form.queued)) again[key] = entry;
    }
    if (Object.keys(again).length > 0) {
      // By themselves, ahead of what is waiting: an answer committed since could be refused in
      // its turn, and that must not be read as these being refused twice.
      form.flight = { entries: again, retried: false, again: true };
      changed(leadId);
      request(leadId, form, form.flight);
      return;
    }
  }
  changed(leadId);
  pump(leadId);
}

// --- leaving a lead --------------------------------------------------------------------------------------

/**
 * The pane stopped showing this lead (another lead, Close, the page gone): every draft that can be
 * sent is sent now. When the queue is empty again, the answers still unsaved are named once in the
 * notice; their drafts stay, so they are back if the lead returns. The exception is an answer to a
 * field that is no longer the rep's to change: it has been named, nothing can save it, and kept it
 * would be named again at every later leave, so it goes.
 */
export function leaveCallForm(leadId: string, name: string | null): void {
  const form = forms.get(leadId);
  if (!form) return;
  for (const key of Object.keys(form.drafts)) {
    const field = form.fields.get(key);
    if (field && !(key in form.marks)) commit(form, field);
  }
  leaving.set(leadId, name);
  changed(leadId);
  const started = epoch;
  // After this turn: a pane that leaves and comes straight back (React's double mount in
  // development, a pane moved in the tree) has not left.
  void Promise.resolve().then(() => {
    if (started === epoch) pump(leadId);
  });
}

/** The pane shows this lead (again): it is not being left. */
export function openCallForm(leadId: string): void {
  leaving.delete(leadId);
}

function settleLeave(leadId: string): void {
  if (!leaving.has(leadId)) return;
  const name = leaving.get(leadId) ?? null;
  leaving.delete(leadId);
  const form = forms.get(leadId);
  if (!form) return;
  const titles = Object.keys(form.marks)
    .filter((key) => form.drafts[key] !== undefined)
    .map((key) => form.fields.get(key)?.title ?? key);
  if (titles.length === 0) return;
  notice = { leadId, text: leftBehindText(name, titles) };
  for (const key of form.gone) {
    delete form.drafts[key];
    delete form.marks[key];
  }
  form.gone.clear();
  changed(leadId);
}

export function dismissCallFormNotice(): void {
  if (notice === null) return;
  notice = null;
  for (const notify of watchers) notify();
}

// --- whose it is -----------------------------------------------------------------------------------------

/** Empties the store: every draft, mark, queue and the notice. A request still away changes
 *  nothing when it comes back. Also puts back the real sender, for a test that replaced it. */
export function resetCallFormStore(): void {
  epoch += 1;
  forms = new Map();
  views = new Map();
  leaving = new Map();
  notice = null;
  account = undefined;
  write = null;
  send = patchFields;
  wait = pause;
  for (const notify of watchers) notify();
}

/** For tests: the request and the pause before a second try, without a network or a clock. */
export function replaceCallFormTransport(next: { send?: Sender; wait?: (ms: number) => Promise<void> }): void {
  if (next.send) send = next.send;
  if (next.wait) wait = next.wait;
}

/**
 * Says whose answers the store holds and how a saved form reaches the query cache. Another account
 * than the last one empties the store first.
 */
export function registerCallFormSaver(owner: string, writer: Writer): void {
  if (account !== undefined && account !== owner) {
    const [keptSend, keptWait] = [send, wait];
    resetCallFormStore();
    [send, wait] = [keptSend, keptWait];
  }
  account = owner;
  write = writer;
}

/**
 * Puts a saved form into the query cache. A read of the same form may be on its way (the
 * half-minute poll, a window coming back into focus) that the server answered before the save, and
 * left alone it would land after this write and put the old value back on screen until the next
 * poll. So it is cancelled first: the saved form stands until a read that started after it.
 */
export function writeSavedForm(client: QueryClient, queryKey: QueryKey, saved: LeadFields): void {
  void client.cancelQueries({ queryKey });
  client.setQueryData(queryKey, saved);
}

/**
 * Called once, by the Phone page's workspace: gives the queue the signed-in person's query cache.
 * The registration outlives the page on purpose, so a save sent as the rep walks away from the
 * page still lands in the cache the contact page reads.
 */
export function useCallFormSaver(): void {
  const { user } = useAuth();
  const owner = accountScope(user);
  const keys = useKeys();
  const client = useQueryClient();
  React.useEffect(() => {
    registerCallFormSaver(owner, (leadId, saved) => writeSavedForm(client, keys.contactFields(leadId), saved));
  }, [owner, keys, client]);
}

// --- reading ---------------------------------------------------------------------------------------------

export function callFormView(leadId: string | null): CallFormView {
  return (leadId !== null && views.get(leadId)) || EMPTY;
}

/** One lead's drafts, marks and save state. */
export function useCallForm(leadId: string | null): CallFormView {
  const read = () => callFormView(leadId);
  return React.useSyncExternalStore(watch, read, read);
}

export function callFormNotice(): LeftBehind | null {
  return notice;
}

/** The notice for answers left unsaved, whatever lead the pane shows now. */
export function useCallFormNotice(): LeftBehind | null {
  return React.useSyncExternalStore(watch, callFormNotice, callFormNotice);
}
